/**
 * locale.js — Shared currency / amount formatters for Granullo.
 *
 * Single source of truth for AED / INR / USD / EUR / GBP / SGD rendering.
 * Anywhere we surface a number to the user, we route it through `formatMoney()`
 * so we get consistent thousand-separators, currency symbols, and locale
 * conventions across the app.
 *
 * Usage:
 *     import { formatMoney, parseLocale, getLocale, setLocale } from '../utils/locale';
 *     formatMoney(9523.81, 'AED')          // "AED 9,523.81"
 *     formatMoney(950000, 'INR')           // "₹9,50,000.00"
 *     formatMoney(10000, 'USD', { compact: true }) // "$10K"
 */

const STORAGE_KEY = 'granullo_locale';

/* Mapping of currency → preferred BCP-47 locale.
   Indian Rupees use en-IN for the lakh/crore grouping (1,00,000). */
const CURRENCY_LOCALE = {
  USD: 'en-US',
  EUR: 'en-IE',
  GBP: 'en-GB',
  AED: 'en-AE',
  INR: 'en-IN',
  SGD: 'en-SG',
};

const KNOWN = Object.keys(CURRENCY_LOCALE);

export function getLocale() {
  if (typeof window === 'undefined') return { currency: 'USD', locale: 'en-US' };
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored && KNOWN.includes(stored)) {
    return { currency: stored, locale: CURRENCY_LOCALE[stored] };
  }
  return { currency: 'USD', locale: CURRENCY_LOCALE.USD };
}

export function setLocale(currency) {
  if (!KNOWN.includes(currency)) return false;
  if (typeof window !== 'undefined') {
    window.localStorage.setItem(STORAGE_KEY, currency);
    // notify listeners (e.g. hook below)
    window.dispatchEvent(new Event('granullo-locale-change'));
  }
  return true;
}

export function listCurrencies() {
  return KNOWN.slice();
}

/**
 * formatMoney — locale-aware money formatter.
 *
 * @param {number|string} amount  Numeric value (string accepted).
 * @param {string} [currency]     ISO 4217 currency code. Defaults to user choice.
 * @param {object} [opts]
 * @param {boolean} [opts.compact]  Use compact notation (1.2K / 1.2M / 1.2Cr).
 * @param {number}  [opts.minimumFractionDigits] (default 2)
 * @param {number}  [opts.maximumFractionDigits] (default 2)
 */
export function formatMoney(amount, currency, opts = {}) {
  const { currency: userCcy, locale: userLocale } = getLocale();
  const ccy = (currency || userCcy || 'USD').toUpperCase();
  const locale = CURRENCY_LOCALE[ccy] || userLocale || 'en-US';

  const num = typeof amount === 'number' ? amount : parseFloat(amount);
  if (Number.isNaN(num)) return '—';

  const fmtOpts = {
    style: 'currency',
    currency: ccy,
    currencyDisplay: 'code',
    minimumFractionDigits: opts.minimumFractionDigits ?? 2,
    maximumFractionDigits: opts.maximumFractionDigits ?? 2,
  };
  if (opts.compact) {
    fmtOpts.notation = 'compact';
    fmtOpts.maximumFractionDigits = 1;
  }

  try {
    return new Intl.NumberFormat(locale, fmtOpts).format(num);
  } catch {
    return `${ccy} ${num.toFixed(2)}`;
  }
}

/** Locale-aware integer formatter (for counts, beneficiaries, etc.) */
export function formatNumber(value, opts = {}) {
  const { locale } = getLocale();
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (Number.isNaN(num)) return '—';
  try {
    return new Intl.NumberFormat(locale, {
      notation: opts.compact ? 'compact' : 'standard',
      maximumFractionDigits: opts.compact ? 1 : 0,
    }).format(num);
  } catch {
    return String(num);
  }
}

/** React hook — re-renders when the locale changes. */
import { useEffect, useState } from 'react';

export function useLocale() {
  const [snapshot, setSnapshot] = useState(getLocale);
  useEffect(() => {
    const handler = () => setSnapshot(getLocale());
    window.addEventListener('granullo-locale-change', handler);
    window.addEventListener('storage', handler);
    return () => {
      window.removeEventListener('granullo-locale-change', handler);
      window.removeEventListener('storage', handler);
    };
  }, []);
  return {
    ...snapshot,
    setCurrency: setLocale,
    currencies: listCurrencies(),
  };
}

export const LOCALE_PARSE = parseLocale;
function parseLocale(s) {
  // Best-effort: pull a number out of a localized string ("AED 9,523.81").
  const cleaned = String(s).replace(/[^\d.\-]/g, '');
  const n = parseFloat(cleaned);
  return Number.isNaN(n) ? null : n;
}
